import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Container,
  Grid,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import BackBtn from "../../widgets/BackBtn";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import DownloadIcon from "@mui/icons-material/Download";
import { ScrollTableContainer } from "../../components/visitors/Visitors";
import { LoadingButton } from "@mui/lab";
import { toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { getHeader } from "../../utils/utils2";
import {
  buildExhibitorUpdateTemplate,
  parseExhibitorUpdateSheet,
} from "../../utils/exhibitorUpdateExcel";
function UpdateExcelReader() {
  const [excelData, setExcelData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false); // Added loading state

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, {
          type: "binary",
          cellDates: true,
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = parseExhibitorUpdateSheet(sheet);
        setExcelData(rows);
        if (rows.length === 0) toast.error("The uploaded file has no data rows.");
      } catch (error) {
        setExcelData([]);
        toast.error(error.message || "The Excel file could not be read.");
      } finally {
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setExcelData([]);
      toast.error("The Excel file could not be read.");
      setLoading(false);
    };
    reader.readAsBinaryString(file);
  };

  const saveList = async () => {
    if (excelData.length === 0) {
      toast.error("No data found!");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_ENDPOINT}/admin/update-exhibitors-from-excel`,
        excelData,
        getHeader()
      );
      toast.success(data.message);
      setTimeout(() => {
        document.location.reload();
      }, 800);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };
  const onCancel = () => {
    document.location.reload();
  };
  return (
    <Box>
      <BackBtn />
      <Container>
        <Typography variant="h6" gutterBottom>
          Update Exhibitor List with Excel File
        </Typography>
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <Button
              startIcon={<ImportExportIcon />}
              variant="contained"
              component="label"
              sx={{ marginBottom: 2 }}
            >
              Import Excel File
              <input
                type="file"
                aria-label="Exhibitor update Excel file"
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
                hidden
              />
            </Button>
          </Grid>
          <Grid item sm={6}>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              component="label"
              sx={{ marginBottom: 2 }}
              onClick={() => {
                XLSX.writeFile(buildExhibitorUpdateTemplate(), "update_template.xlsx");
              }}
            >
              Download Excel Template
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          // Show loading spinner
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {excelData.length === 0 && (
              <Stack gap={2} direction="column">
                <Typography variant="h6" gutterBottom>
                  Some points to consider before uploading:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="1. Use existing exhibitor IDs in the first column. Download the template for examples." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Fill only the fields you want to update: Hall Number, Stand Number, Space Type, Exhibit Arrival, Material Arrival, Dismantling, Stand Size or Product Categories. Blank cells leave existing values unchanged." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. For Product Categories, enter comma-separated product codes, such as A23.01, A01.01. A filled cell replaces all existing categories for that exhibitor. Include every code you want to keep." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="4. Include at least one data row, with an Exhibitor ID and at least one field to update. Only the first worksheet is imported." />
                  </ListItem>
                </List>
              </Stack>
            )}
            {excelData.length > 0 && (
              <>
                <Typography gutterBottom variant="body1">
                  Please review the following list before saving the list to the
                  database:
                </Typography>
                <Divider />
              </>
            )}
            <ScrollTableContainer>
              {excelData.length > 0 && (
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {Object.keys(excelData[0]).map((key) => (
                        <TableCell key={key} sx={{ fontWeight: "bold" }}>
                          {key}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelData.map((row, index) => (
                      <TableRow key={index}>
                        {Object.entries(row).map(([key, value], idx) => (
                          <TableCell key={idx}>
                            {value?.toLocaleString()}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollTableContainer>

            {excelData.length > 0 && (
              <>
                <Typography mt={2} mb={2} variant="body1">
                  If the contents in the list are correct, then you can proceed
                  to save the list:
                </Typography>
                <Stack direction={"row"} gap={2}>
                  <Button onClick={onCancel} variant="outlined">
                    Cancel
                  </Button>
                  <LoadingButton
                    onClick={saveList}
                    loading={saving}
                    variant="contained"
                  >
                    Save List
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default UpdateExcelReader;
